// src/context/DialogContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

const DialogContext = createContext();

// App-wide replacement for window.alert()/window.confirm()/window.prompt()
// so pop-ups match the rest of the UI instead of the browser's native (and
// blockable) dialogs.
export function DialogProvider({ children }) {
  const [dialog, setDialog] = useState(null); // { type, title, message, variant, confirmText, cancelText, resolve }
  const [promptValue, setPromptValue] = useState('');

  const alert = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setDialog({
        type: 'alert',
        message,
        title: options.title || 'Notice',
        variant: options.variant || 'primary',
        resolve,
      });
    });
  }, []);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setDialog({
        type: 'confirm',
        message,
        title: options.title || 'Please confirm',
        variant: options.variant || 'danger',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        resolve,
      });
    });
  }, []);

  // Resolves with the entered text, or null if cancelled. `options.required`
  // (default true) keeps the confirm button disabled until non-blank text is
  // entered - used for accountability comments that must not be empty.
  const prompt = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setPromptValue(options.defaultValue || '');
      setDialog({
        type: 'prompt',
        message,
        title: options.title || 'Please provide details',
        variant: options.variant || 'primary',
        confirmText: options.confirmText || 'Submit',
        cancelText: options.cancelText || 'Cancel',
        required: options.required !== false,
        placeholder: options.placeholder || '',
        resolve,
      });
    });
  }, []);

  const close = (result) => {
    if (dialog) dialog.resolve(result);
    setDialog(null);
    setPromptValue('');
  };

  const promptInvalid = dialog?.type === 'prompt' && dialog.required && !promptValue.trim();

  return (
    <DialogContext.Provider value={{ alert, confirm, prompt }}>
      {children}
      <Modal
        show={!!dialog}
        onHide={() => close(dialog?.type === 'alert' ? undefined : (dialog?.type === 'prompt' ? null : false))}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{dialog?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-line' }}>
          {dialog?.message}
          {dialog?.type === 'prompt' && (
            <Form.Control
              as="textarea"
              rows={3}
              className="mt-2"
              value={promptValue}
              placeholder={dialog.placeholder}
              onChange={(e) => setPromptValue(e.target.value)}
              autoFocus
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          {(dialog?.type === 'confirm' || dialog?.type === 'prompt') && (
            <Button variant="secondary" onClick={() => close(dialog.type === 'prompt' ? null : false)}>
              {dialog.cancelText}
            </Button>
          )}
          <Button
            variant={dialog?.variant || 'primary'}
            disabled={promptInvalid}
            onClick={() => close(dialog?.type === 'confirm' ? true : (dialog?.type === 'prompt' ? promptValue.trim() : undefined))}
          >
            {dialog?.type === 'confirm' || dialog?.type === 'prompt' ? dialog.confirmText : 'OK'}
          </Button>
        </Modal.Footer>
      </Modal>
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within a DialogProvider');
  return ctx;
}
